import EmptyState from '@/components/EmptyState';
import { FileSearchIcon } from '@/icons';
import { formatDateRange } from '@/utils';
import { Avatar, Flex, Typography } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useGetHighSalaryLowOutputUsersQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import type { UnderperformerUser } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.type';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const { Text } = Typography;

const HighSalaryLowOutput = ({
  department_id,
  office_id,
  filter: allFilters,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const filter = allFilters.highSalaryLowOutput;

  const { data: underperformingData, error } =
    useGetHighSalaryLowOutputUsersQuery({
      start_date: filter.start_date || '',
      end_date: filter.end_date || '',
      department_id,
      office_id,
    });
  useError(error, 'Failed to fetch high salary low output users');

  const underperformers =
    underperformingData?.data?.data?.underperformers || [];

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full">
      <Flex
        justify="space-between"
        align="center"
        className="mb-4 text-white p-2"
      >
        <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight">
          High Salary Low Output
          <span className="text-theme-primary block text-sm">
            {formatDateRange(filter.start_date, filter.end_date)}
          </span>
        </h4>
        <CustomRangePicker
          picker="month"
          startDate={filter.start_date}
          endDate={filter.end_date}
          onChange={(start, end) => {
            const s = dayjs(start).startOf('month').format('YYYY-MM-DD');
            const e = dayjs(end).endOf('month').format('YYYY-MM-DD');
            setFilter('highSalaryLowOutput', {
              start_date: s,
              end_date: e,
            });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'month').endOf('month')
          }
          isChanged={isFilterChanged('highSalaryLowOutput')}
          onReset={() => resetFilter('highSalaryLowOutput')}
        />
      </Flex>

      {/* List */}
      <div className="mt-6 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
        {underperformers.length === 0 ? (
          <div className="flex items-center justify-center pt-3">
            <EmptyState
              className="py-0"
              icon={<FileSearchIcon size={40} />}
              title="No Underperformers Found"
              description="No data matches the High Salary Low Output criteria for this timeframe."
            />
          </div>
        ) : (
          underperformers.map((item: UnderperformerUser, index: number) => {
            // Normalizing for bar width, but show actual value. Gap can be large.
            const barWidth = Math.min(
              100,
              item.salary_above_avg_percentage / 4,
            ); // Scaling for visualization

            return (
              <Flex key={item.user_id} align="center" gap={16} className="mb-6">
                {/* Avatar */}
                <div className="flex flex-col items-center w-[80px] text-center">
                  <Avatar src={item.photo} size={42} className="shrink-0">
                    {item.name?.charAt(0)}
                  </Avatar>
                  <h6 className="text-[12px] font-medium mt-0.5 text-white text-center w-full truncate">
                    {item.name}
                  </h6>
                  <p className="text-[11px] -mb-1">
                    ID: {item.employee_id || '--'}
                  </p>
                  <p className="text-[11px]">
                    Department: {item?.department_name || '--'}
                  </p>
                </div>

                {/* Progress */}
                <div className="flex-1 max-w-[60%]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${barWidth}%` }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    className="relative h-11 rounded-r-lg bg-[#C96808]"
                  >
                    <Text className="!text-white text-2xl font-semibold block leading-tight text-nowrap absolute left-full mt-1.5 ml-2">
                      {item.avg_productive_hours.toFixed(1)}h
                    </Text>
                  </motion.div>
                </div>
              </Flex>
            );
          })
        )}
      </div>
    </div>
  );
};

export default HighSalaryLowOutput;
